import { yDefer } from '../y/util';
import { UIViewSpec } from './view';

export type UIEasing = 'linear' | 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'cubic-bezier(0, 0, 0.2, 1)';
export type UIAnimationName = 'slide-h' | 'slide-v' | 'flash-scale' | 'flash-invalid' | 'flash-invalid-code' | 'shake' | 'rotate-in' | 'rotate-out' | 'zoom-in' | 'explode' | 'pager-from-right' | 'pager-from-left' | 'toast-in' | 'toast-out' | 'fade-out' | 'fade-in' | 'slide-left' | 'slide-right' | 'slide-top' | 'slide-bottom' | 'ui-pan' | 'ui-tooltip-in' | 'ui-tooltip-out' | 'transition-out' | 'transition-in';
// TODO: default durations: 0.15, 0.15, 0.4, 0.3, 0.3, 0.3, 0.4, 0.3, 0.4, 0.4, 0.2, 0.3, 0.3, 0.4

export interface UIAnimationOptions {
    reverse?: boolean;
    easing?: UIEasing;
    delay?: number;
    duration?: number;
}

export interface UIAnimation extends UIAnimationOptions {
    what: UIViewSpec;

    style?: string;
    property?: string;
    from?: number;
    to?: number;
    ignore_end?: boolean;

    animation?: UIAnimationName;
}

export interface UIInterpolationCurve<Value = number> {
    T: number;
    fn: (x: number) => Value;
}

interface UIStepAnimation {
    from: number;
    to: number;
    duration: number;
    setter?: (v: number) => void;
    finish?: () => void;

    start_time?: number;
    completion?: () => void;
}

interface UICSSAnimation {
    element: HTMLElement;
    style: string;
    duration: number;
    ignore_end?: boolean;
}

const builtin_animations: { [name: string]: ((options: UIAnimation) => UIStepAnimation[]) } = {
    'slide-h': (options) => {
        const elem = options.what.ui_elem;
        elem.style.width = '0';
        return [{
            from: 0, to: elem.scrollWidth, duration: 0.2, setter: (v) => {
                elem.style.width = v + 'px';
            }
        }];
    },
    'slide-v': (options) => {
        if(options.reverse) {
            const elem = options.what.ui_elem;
            const height = elem.offsetHeight;
            elem.style.overflowY = 'hidden';
            // const yOffset = window.scrollY;
            return [{
                from: height, to: 0, duration: 0.2, setter: (v) => {
                    elem.style.height = v + 'px';
                    // document.body.scrollTop = yOffset + v;
                    // document.documentElement.scrollTop = yOffset + v;
                }, finish: () => {
                    elem.style.height = '';
                }
            }];
        } else {
            const elem = options.what.ui_elem;
            const height = elem.offsetHeight;
            elem.style.height = '0';
            elem.style.overflowY = 'hidden';
            // const yOffset = window.scrollY;
            return [{
                from: 0, to: height, duration: 0.2, setter: (v) => {
                    elem.style.height = v + 'px';
                    // document.body.scrollTop = yOffset + v;
                    // document.documentElement.scrollTop = yOffset + v;
                }, finish: () => {
                    elem.style.overflowY = '';
                }
            }];
        }
    }
}

const style_is_dimension: { [name: string]: boolean } = {
    'top': true,
    'bottom': true,
    'left': true,
    'right': true,
    'width': true,
    'height': true,
    'opacity': false
}

let frame_requested = false;
let step_animations: UIStepAnimation[] = [];

function frameHandler(ts: number) {
    const old_animations = step_animations;
    step_animations = [];
    for(const a of old_animations) {
        if(!a.start_time)
            a.start_time = ts;
        let step = (ts - a.start_time) / (1000 * a.duration);

        if(step >= 1) step = 1;

        a.setter && a.setter(a.from + step * (a.to - a.from));

        if(step === 1) {
            a.completion && a.completion();
            a.finish && a.finish();
        } else {
            step_animations.push(a);
        }
    }

    if(step_animations.length)
        requestAnimationFrame(frameHandler);
    else
        frame_requested = false;
}

function doAnimate(max_duration: number, step: UIStepAnimation[], css: UICSSAnimation[], completion?: () => void) {
    const token = {};
    let max_is_css = false;

    if(css.length) {
        let max_css = css[0];
        for(const anim of css) {
            if((anim.duration > max_css.duration || max_css.ignore_end) && !anim.ignore_end)
                max_css = anim;
            anim.element.style.animation = anim.style;
            anim.element.ui_anim_token = token;
        }

        max_is_css = (max_css.duration === max_duration);

        let listener = () => {
            if(max_is_css && completion)
                completion();

            for(const anim of css) {
                if(anim.element.ui_anim_token === token)
                    anim.element.style.animation = '';
            }
            max_css.element.removeEventListener('animationend', listener);
        };

        max_css.element.addEventListener('animationend', listener);
    }

    if(step.length) {
        let max_step = step[0];
        for(const anim of step) {
            if(anim.duration > max_step.duration)
                max_step = anim;
            step_animations.push(anim);
        }

        if(!max_is_css)
            max_step.completion = completion;

        if(!frame_requested) {
            requestAnimationFrame(frameHandler);
            frame_requested = true;
        }
    }
}

export function uiAnimate(animations: UIAnimation[], completion?: () => void) {
    let max_duration = 0;
    const css: UICSSAnimation[] = [];
    const step: UIStepAnimation[] = [];

    for(const anim of animations) {
        let duration = anim.duration;
        if(duration == undefined)
            duration = 0.3;
        if(duration <= 0) continue;
        duration += anim.delay || 0;

        if(anim.animation) {
            let builtin = builtin_animations[anim.animation];

            if(builtin) {
                step.push(...builtin(anim));
            } else {
                let style = anim.animation + ' ' + duration + 's';
                if(anim.easing)
                    style += ' ' + anim.easing;
                if(anim.delay)
                    style += ' ' + anim.delay + 's';
                if(anim.reverse)
                    style += ' reverse';

                css.push({ element: anim.what.ui_elem, style: style, duration: duration, ignore_end: anim.ignore_end });
            }
        } else {
            let setter: ((v: number) => void) | undefined;
            if(anim.property) {
                let elem = anim.what.ui_elem as any;
                let property = anim.property;
                setter = (v) => { elem[property] = v };
            } else if(anim.style) {
                let elem = anim.what.ui_elem;
                let style = anim.style;
                setter = (v) => { elem.style.setProperty(style, style_is_dimension[style] ? v + 'px' : v + '') };
            }

            step.push({ from: anim.from!, to: anim.to!, duration: duration, setter: setter });
        }

        if(duration > max_duration)
            max_duration = duration;
    }

    if(max_duration)
        doAnimate(max_duration, step, css, completion);
    else if(completion)
        completion();
}

export function uiAnimateTransition(what: UIViewSpec, c: string) {
    return new Promise<void>((resolve, _reject) => {
        const elem = what.ui_elem;
        elem.classList.add(c);
        const listener = () => {
            resolve();
            elem.removeEventListener('transitionend', listener);
        }
        elem.addEventListener('transitionend', listener);
    });
}

export function uiScrollTo(y: number | UIViewSpec, duration: number = 0.3) {
    if(typeof y !== 'number')
        y = Math.max(y.ui_elem.ui_docRect().top - 100, 0);
    uiAnimate([{ what: document.body, property: 'scrollTop', from: document.body.scrollTop, to: y, duration: duration },
    { what: document.documentElement, property: 'scrollTop', from: document.documentElement.scrollTop, to: y, duration: duration }]);
}

export function uiInterpolate<Value>(curve: UIInterpolationCurve<Value>, callback: (x: Value) => void | boolean, promise: true): Promise<void>
export function uiInterpolate<Value>(curve: UIInterpolationCurve<Value>, callback: (x: Value) => void | boolean): void
export function uiInterpolate<Value>(curve: UIInterpolationCurve<Value>, callback: (x: Value) => void | boolean, promise?: true): Promise<void> | void {
    const T = curve.T;
    const fn = curve.fn;

    if(T <= 0) {
        callback(fn(0));
        return promise && Promise.resolve();
    }

    const deferred = promise && yDefer();

    const start = performance.now();
    const end = start + T * 1000;

    const cb = (t: number) => {
        if(t > end) t = end;

        const cancel = callback(fn((t - start) / 1000));

        if(t < end && !cancel) {
            requestAnimationFrame(cb);
        } else {
            deferred && deferred.resolve();
        }
    }

    requestAnimationFrame(cb);

    return deferred && deferred.promise;
}